<script setup>
import {
  PhxButton,
  PhxTabPanel,
  PhxTabs,
  PhxTransactionPanel,
  PhxWizardStep as PhxTransactionPanelStep,
} from '@phx-styleguides/iwc';
import { computed, ref, watchEffect } from 'vue';
import { ceil, get } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import ProductSelector from '@maison/components/organisms/transactionPanel/ProductSelector.vue';
import ProductDetail from '@maison/components/organisms/transactionPanel/ProductDetail.vue';
import { parse as queryStringParse } from 'query-string';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    required: true,
  },
  tabs: {
    type: Array,
    required: true,
  },
  contactsPanelId: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const currentTab = ref('');
const contentId = ref(`${props.id}-content`);
const tabsContentTeleportTarget = ref(null);
const selectedProduct = ref(null);
const initialStep = ref(0);

const addToBagLabel = computed(() => t('TransactionPanel.addToBag'));

const closeLabel = computed(() => t('Accessibility.modal.close'));

const selectedProductData = computed(
  () => props.tabs.flatMap((tab) => tab.products).find((product) => product.value === selectedProduct.value).product,
);

// We open automatically the panel to the last step if the product is in the URL and available in the tabs.
const queryString = queryStringParse(window.location.search);
if (queryString.product) {
  const productTab = props.tabs.find((tab) => tab.products.some((p) => p.id === queryString.product));
  if (productTab) {
    currentTab.value = productTab.id;
    selectedProduct.value = queryString.product;
    initialStep.value = 2;
  }
}

const onTabChange = (tab) => {
  currentTab.value = tab;
};

watchEffect(() => {
  if (currentTab.value === '') {
    const position = ceil(props.tabs.length / 2) - 1;
    currentTab.value = get(props.tabs, [position, 'id'], 0);
  }
});
</script>

<template>
  <phx-transaction-panel :initial-step="initialStep">
    <template #default="{ goToNextStep, goToPreviousStep, goToStep }">
      <phx-transaction-panel-step class="transaction-panel-step transaction-panel-step__step-one">
        <hr class="divider divider--color-foreground mask-reveal-to-right" />

        <div class="transaction-panel-step__header fade-in">
          <h3 class="title-caption">{{ title }}</h3>
          <span class="title-caption">{{ subtitle }}</span>
        </div>

        <div class="transaction-panel-step__content"></div>

        <div class="transaction-panel-step__footer mask-reveal-to-top">
          <phx-button
            class="btn--plain btn--primary hidden md:block"
            :label="addToBagLabel"
            @click="goToNextStep"
          ></phx-button>

          <phx-button
            class="step-one__mobile-trigger title-caption uppercase md:hidden scale-x-up"
            bare
            @click="goToNextStep"
          >
            <span>{{ subtitle }}</span>
            <span>{{ addToBagLabel }}</span>
          </phx-button>
        </div>
      </phx-transaction-panel-step>

      <phx-transaction-panel-step
        class="transaction-panel-step transaction-panel-step__step-two"
        scroll-into-view="end"
      >
        <hr class="divider hidden md:block divider--color-foreground" />

        <div class="transaction-panel-step__header">
          <h3 class="title-caption hidden md:block">{{ title }}</h3>
          <phx-button class="btn--bare transaction-panel__close-button" :label="closeLabel" @click="goToStep(0)" />
          <h3 class="step-two__mobile-title heading-small md:hidden">{{ title }}</h3>
        </div>

        <div class="transaction-panel-step__content">
          <phx-tabs
            :id="`${id}-tabs`"
            layout="stretch"
            class="step-two__tabs"
            orientation="vertical"
            :value="currentTab"
            :show-indicator="false"
            @change="onTabChange"
          >
            <template v-for="tab in tabs" :key="tab.id">
              <phx-tab-panel :id="tab.id" :label="tab.label">
                <product-selector :id="`${tab.id}-product-field`" v-model="selectedProduct" :products="tab.products" />
              </phx-tab-panel>
            </template>
          </phx-tabs>
        </div>

        <div class="transaction-panel-step__footer">
          <phx-button
            class="btn--plain btn--primary"
            :label="addToBagLabel"
            :disabled="selectedProduct === null"
            @click="goToNextStep"
          />
        </div>
      </phx-transaction-panel-step>

      <phx-transaction-panel-step class="transaction-panel-step transaction-panel-step__step-three">
        <hr class="divider hidden md:block divider--color-foreground" />

        <div class="transaction-panel-step__header">
          <phx-button
            class="btn--bare btn--fade"
            :label="t('TransactionPanel.back')"
            :icon="{ start: { icon: 'arrow-left', size: 'sm' } }"
            @click="goToPreviousStep"
          />

          <phx-button
            class="btn--bare btn--fade transaction-panel__close-button"
            :label="closeLabel"
            @click="goToStep(0)"
          />
        </div>

        <product-detail
          v-if="selectedProduct"
          :id="`${id}-product-detail`"
          v-bind="selectedProductData"
          :key="selectedProductData.reference"
          :contacts-panel-id="contactsPanelId"
        />
      </phx-transaction-panel-step>
    </template>
  </phx-transaction-panel>
</template>
